import React from "react";
import { Phone, ArrowRight, Star, Check } from "lucide-react";
import Icon from "./Icon";
import { contact } from "../mock";
import { useLang, heroImages } from "../i18n";

export function Hero() {
  const { t } = useLang();
  const h = t.hero;

  return (
    <section id="start" className="relative isolate overflow-hidden bg-[#1c1d20]">
      {/* Ein einziges Bild: die Hausfront, formatfuellend */}
      <div
        className="absolute inset-0 bg-cover bg-[center_38%]"
        style={{ backgroundImage: `url(${heroImages.house})` }}
        aria-hidden="true"
      />
      {/* Lesbarkeit: mobil flaechig, ab lg von links auslaufend */}
      <div className="absolute inset-0 bg-[#1c1d20]/60 lg:hidden" aria-hidden="true" />
      <div
        className="absolute inset-0 hidden lg:block bg-gradient-to-r from-[#1c1d20]/85 from-0% via-[#1c1d20]/55 via-48% to-transparent to-72%"
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#1c1d20]/80 to-transparent"
        aria-hidden="true"
      />
      <div
        className="absolute -top-28 left-0 h-[420px] w-[620px] rounded-full bg-[#d9b64e]/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:py-32">
        <div className="max-w-2xl">
          <span
            data-reveal
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase text-white/90 backdrop-blur-sm"
          >
            <Star className="h-3.5 w-3.5 text-[#d9b64e]" />
            {h.badge}
          </span>

          <h1
            data-reveal
            data-delay="1"
            className="mt-7 font-display text-[2.7rem] leading-[1.04] text-white [text-shadow:0_3px_24px_rgba(12,13,15,0.6)] sm:text-6xl lg:text-[4.4rem]"
          >
            {h.titleTop}
            <span className="block text-[#d9b64e]">{h.titleBottom}</span>
          </h1>

          <span
            data-reveal
            data-delay="2"
            className="mt-7 block h-[3px] w-20 rounded-full bg-[#d9b64e]"
            aria-hidden="true"
          />

          <p
            data-reveal
            data-delay="2"
            className="mt-7 max-w-xl text-[17px] leading-relaxed text-white/90 [text-shadow:0_2px_14px_rgba(12,13,15,0.75)]"
          >
            {h.lead}
          </p>

          <div data-reveal data-delay="3" className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="/#kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#2a2b2f] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#eceef1] hover:shadow-[0_18px_34px_-18px_rgba(0,0,0,0.7)]"
            >
              {h.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`tel:${contact.phoneLink}`}
              className="btn-ghost-light inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold backdrop-blur-sm"
            >
              <Phone className="h-4 w-4" /> {contact.phoneDisplay}
            </a>
          </div>

          <div data-reveal data-delay="4" className="mt-11 flex flex-wrap gap-x-8 gap-y-4">
            {h.checks.map((c) => (
              <span key={c} className="inline-flex items-center gap-2 text-sm text-white/90 [text-shadow:0_2px_12px_rgba(12,13,15,0.8)]">
                <Check className="h-4 w-4 text-[#d9b64e]" /> {c}
              </span>
            ))}
          </div>
        </div>

        {/* Preis-Karte schwebt ueber dem Foto */}
        <div
          data-reveal
          data-delay="3"
          className="mt-12 w-fit lg:absolute lg:bottom-10 lg:right-0 lg:mt-0"
        >
          <span className="mb-3 inline-block rounded-full bg-[#d9b64e] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#2f2600]">
            {h.stamp}
          </span>
          <div className="rounded-2xl border border-white/15 bg-white/95 px-6 py-5 shadow-[0_26px_60px_-28px_rgba(0,0,0,0.75)] backdrop-blur-sm">
            <p className="eyebrow text-[#55575e]">{h.priceLabel}</p>
            <p className="mt-1 font-display text-4xl leading-none text-[#2a2b2f]">{h.priceValue}</p>
            <p className="mt-1.5 text-[13px] text-slate-500">{h.priceNote}</p>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 bg-[#1c1d20] py-4 marquee-wrap overflow-hidden">
        <div className="marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex gap-12 pr-12" aria-hidden={dup === 1}>
              {h.marquee.map((m) => (
                <span key={m} className="whitespace-nowrap text-[13px] tracking-wide text-white/80">
                  {m}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function QuickFacts() {
  const { t } = useLang();
  return (
    <section className="surface-soft py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {t.quickFacts.map((f, i) => (
            <div
              key={f.title}
              data-reveal
              data-delay={String((i % 5) + 1)}
              className="card-lift rounded-2xl border border-slate-200/80 bg-white p-6"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#f1f2f4] text-[#55575e]">
                <Icon name={f.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-[15px] font-semibold text-[#2a2b2f]">{f.title}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-500">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Welcome() {
  const { t } = useLang();
  const w = t.welcome;
  return (
    <section id="pension" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div data-reveal className="relative">
              <div className="img-zoom overflow-hidden rounded-[1.5rem] border border-slate-200 shadow-[0_30px_70px_-40px_rgba(42,43,47,0.5)]">
                <img
                  src={heroImages.room}
                  alt={w.roomAlt}
                  loading="eager"
                  decoding="async"
                  className="pixel-soft h-[360px] w-full object-cover sm:h-[480px]"
                />
              </div>
              <div className="absolute -bottom-7 right-4 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-[0_24px_50px_-28px_rgba(42,43,47,0.5)]">
                <p className="font-display text-2xl text-[#2a2b2f]">{w.badgeValue}</p>
                <p className="text-[12px] uppercase tracking-wider text-slate-500">{w.badgeLabel}</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p data-reveal className="eyebrow text-[#55575e]">
              {w.eyebrow}
            </p>
            <h2
              data-reveal
              data-delay="1"
              className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl"
            >
              {w.title}
            </h2>

            <ul data-reveal data-delay="2" className="mt-7 space-y-2">
              {w.address.map((a) => (
                <li key={a} className="flex items-start gap-3 text-[15px] text-slate-600">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#55575e]" />
                  {a}
                </li>
              ))}
            </ul>

            <div data-reveal data-delay="3" className="mt-8 space-y-5">
              {w.paragraphs.map((p) => (
                <p key={p.slice(0, 24)} className="text-[16px] leading-[1.8] text-slate-600">
                  {p}
                </p>
              ))}
            </div>

            <div
              data-reveal
              data-delay="4"
              className="mt-9 rounded-2xl border-l-4 border-[#55575e] bg-[#f5f6f8] px-6 py-5"
            >
              <p className="font-display text-2xl text-[#2a2b2f]">{w.highlight}</p>
              <p className="mt-1 text-[14px] text-slate-600">
                {w.languagesLabel}: {w.languages.join(w.joiner)}
              </p>
            </div>

            <div data-reveal data-delay="5" className="mt-10 grid gap-4 sm:grid-cols-3">
              {t.roomFeatures.map((r) => (
                <div
                  key={r.label}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors duration-300 hover:border-[#b6b8be] hover:bg-[#fafafb]"
                >
                  <Icon name={r.icon} className="h-4 w-4 text-[#55575e]" />
                  <span className="text-[13.5px] font-medium text-slate-700">{r.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
